import { union, unionBy } from "lodash";
import type { AppDispatch } from "../../../../Application/globaltypes/redux";
import DataService from "../../../../Application/services/dataServices/typedDataService";
import {
  type MsGraphFilterOptionsMap,
  TypeOptionsEnum,
} from "../../Configuration/MsGraphIntegrationFilterBuilder/helpers";
import {
  fetchBegin,
  fetchFailure,
  fetchFilterCriteriaSuccess,
  fetchSuccess,
} from "../slices/msGraphIntegrationFilterSlice";

export const fetchMsGraphIntegrationFilterAction = () => async (dispatch: AppDispatch) => {
  dispatch(fetchBegin());
  try {
    const entries = await Promise.all([
      fetchOption(TypeOptionsEnum.JobTitle, DataService.integrations.getJobTitles, mapStrings),
      fetchOption(TypeOptionsEnum.CompanyName, DataService.integrations.getCompanyNames, mapStrings),
      fetchOption(TypeOptionsEnum.Department, DataService.integrations.getDepartments, mapStrings),
      fetchOption(TypeOptionsEnum.Country, DataService.integrations.getCountries, mapStrings),
      fetchOption(TypeOptionsEnum.OfficeLocation, DataService.integrations.getOfficeLocations, mapStrings),
      fetchOption(TypeOptionsEnum.License, DataService.users.getLicenses, mapLicenses),
    ]);
    const payload: MsGraphFilterOptionsMap = Object.fromEntries(entries);
    dispatch(fetchSuccess(payload));
  } catch (error) {
    dispatch(fetchFailure(error as Error));
  }
};

export const fetchFilterGroupAction = (groupIds: string[], term?: string) => async (dispatch: AppDispatch) => {
  try {
    const searchResult = await DataService.users.getAccountGroups({ top: 50, skip: 0, term });
    let selectedResult: { name: string; id: string }[] = [];

    if (groupIds.length) {
      selectedResult = await DataService.users.getAccountGroups({ ids: groupIds, top: groupIds.length, skip: 0 });
    }
    const combined = unionBy(searchResult, selectedResult, "id");

    dispatch(fetchFilterCriteriaSuccess({ type: TypeOptionsEnum.Group, items: mapGroups(combined) }));
  } catch (error) {
    dispatch(fetchFailure(error as Error));
  }
};

export const fetchFilterUsersEmailsAction = (emails: string[], term?: string) => async (dispatch: AppDispatch) => {
  try {
    const searchResult = await DataService.integrations.getUsersEmails({ top: 50, skip: 0, term });
    let selectedResult: string[] = [];

    if (emails.length) {
      selectedResult = await DataService.integrations.getUsersEmails({ email: emails, top: emails.length, skip: 0 });
    }
    const combined = union(searchResult, selectedResult);

    dispatch(
      fetchFilterCriteriaSuccess({
        type: TypeOptionsEnum.Manager,
        items: mapStrings(combined),
      }),
    );
  } catch (error) {
    dispatch(fetchFailure(error as Error));
  }
};

const fetchOption = async <T>(
  key: TypeOptionsEnum,
  fetcher: () => Promise<T>,
  mapper: (data: T) => { text: string; value: string }[],
) => {
  const resp = await fetcher();
  return [key, mapper(resp)];
};

const mapStrings = (res: string[]) => res.map((x) => ({ text: x, value: x }));
const mapLicenses = (res: { title: string; skuId: string }[]) =>
  res.map(({ title, skuId }) => ({ text: title, value: skuId }));
const mapGroups = (res: { name: string; id: string }[]) => res.map(({ name, id }) => ({ text: name, value: id }));
